/**
 * Phone validation/formatting aligned with user profile UX.
 *
 * Validation is digit-count based (no leading + required).
 * US display: (555) 123-4567
 * International (>10 digits, non-US-style): +{digits}
 */

export function getPhoneDigits (phoneValue) {
  return String(phoneValue || '').replace(/\D/g, '')
}

/**
 * @returns {string|null} Error message, or null when valid.
 */
export function validatePhoneNumber (phoneValue, { required = true } = {}) {
  const raw = String(phoneValue ?? '')
  if (!raw.trim()) {
    return required ? 'Phone number is required' : null
  }
  const digits = getPhoneDigits(raw)
  const isUSNumber = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
  const isInternationalNumber = digits.length >= 10 && digits.length <= 15
  if (!isUSNumber && !isInternationalNumber) {
    return 'Please enter a valid phone number (10-15 digits)'
  }
  return null
}

export function isValidPhoneNumber (phoneValue, options) {
  return validatePhoneNumber(phoneValue, options) === null
}

/**
 * Display formatting (profile / MFA SMS).
 * Partial input (not yet a complete US or >10-digit international) is returned unchanged.
 */
export function formatPhoneNumber (value) {
  const raw = String(value ?? '')
  const digits = getPhoneDigits(raw)
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
    const national = digits.slice(-10)
    const areaCode = national.slice(0, 3)
    const firstPart = national.slice(3, 6)
    const lastPart = national.slice(6)
    return `(${areaCode}) ${firstPart}-${lastPart}`
  }
  if (digits.length > 10) {
    return `+${digits}`
  }
  return raw
}

/**
 * When loading an existing phone: strip a leading "+1" prefix, then format.
 */
export function formatPhoneNumberForInitialLoad (value) {
  let v = String(value || '').trim()
  if (v.startsWith('+1')) {
    v = v.slice(2).trim()
  }
  return formatPhoneNumber(v)
}

/** Mask for MFA saved-destination display: (***) ***-1234 (US) or +***last4 (intl). */
export function maskPhoneNumber (input) {
  const raw = String(input || '')
  if (!isValidPhoneNumber(raw, { required: true })) {
    return raw
  }
  const digits = getPhoneDigits(raw)
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
    return '(***) ***-' + digits.slice(-4)
  }
  if (digits.length > 4) {
    return '+' + '*'.repeat(Math.min(digits.length - 4, 12)) + digits.slice(-4)
  }
  return `+${digits}`
}

/** @deprecated Use getPhoneDigits / formatPhoneNumber — kept for older call sites. */
export function extractNanpDigits (input) {
  const digits = getPhoneDigits(input)
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  if (digits.length === 10) {
    return digits
  }
  return digits.slice(0, 10)
}

/** @deprecated Use formatPhoneNumber */
export function formatNanpPhone (input) {
  return formatPhoneNumber(input)
}

/** @deprecated Use isValidPhoneNumber */
export function isValidNanpPhone (input) {
  return isValidPhoneNumber(input)
}

/** @deprecated Use maskPhoneNumber */
export function maskNanpPhone (input) {
  return maskPhoneNumber(input)
}
